// src/components/agent/AddressAutocomplete.js
// Wraps a styled <input> with Google Places autocomplete (US addresses).
// On selection, fires onSelect({ line1, city, state, zip }) so the parent
// can populate all four address fields at once.
import React, { useEffect, useRef } from 'react';

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid rgba(107,30,46,0.1)',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: "'DM Sans', sans-serif",
  background: '#FFFFFF',
  color: '#1C1C1C',
  outline: 'none',
  width: '100%'
};

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, style, autoFocus }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    let cleanup = null;
    let cancelled = false;
    let pollTimer = null;

    const tryInit = () => {
      if (cancelled || !ref.current) return;
      if (!window.google?.maps?.places?.Autocomplete) {
        // The Google Maps script is async-loaded in index.html. If it hasn't
        // landed yet, poll every 200ms (max ~10s) until it does.
        pollTimer = setTimeout(tryInit, 200);
        return;
      }
      const ac = new window.google.maps.places.Autocomplete(ref.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address']
      });
      const listener = ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place?.address_components) return;
        const parts = parseComponents(place.address_components);
        if (onSelect) onSelect(parts, place.formatted_address || '');
      });
      cleanup = () => {
        if (window.google?.maps?.event) {
          window.google.maps.event.removeListener(listener);
        }
      };
    };

    tryInit();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (cleanup) cleanup();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <input
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete="off"
      style={{ ...inputStyle, ...style }}
    />
  );
}

function parseComponents(components) {
  const get = (type) => components.find(c => c.types.includes(type));
  const num = get('street_number')?.short_name || '';
  const route = get('route')?.long_name || '';
  const subpremise = get('subpremise')?.long_name || '';
  const line1 = [num, route, subpremise].filter(Boolean).join(' ');
  return {
    line1,
    city: get('locality')?.long_name
       || get('sublocality')?.long_name
       || get('postal_town')?.long_name
       || get('administrative_area_level_2')?.long_name
       || '',
    state: get('administrative_area_level_1')?.short_name || '',
    zip: get('postal_code')?.long_name || ''
  };
}
